import React, { useState } from 'react';
import { todayKST, nowKST, dayOfWeekUTC, daysInMonth as _daysInMonth, toDateOnlyUtc } from '../utils/date';

// 요일 헤더: 일요일부터 시작
const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

// 연도, 월(0-indexed), 일 → YYYY-MM-DD
function toYmd(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// YYYY-MM-DD 문자열 → Date 객체 (UTC 자정 기준)
function parseYmd(ymd) {
  // toDateOnlyUtc: utils/date.js 사용 (new Date 직접 사용 금지)
  return toDateOnlyUtc(ymd);
}

// 해당 월의 첫날 요일(0=일)과 전체 일수를 반환
function buildCalendarMeta(year, month) {
  // dayOfWeekUTC, daysInMonth: utils/date.js 사용 (new Date 직접 사용 금지)
  const ymd = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  return {
    firstDay: dayOfWeekUTC(ymd),
    daysInMonth: _daysInMonth(year, month + 1),
  };
}

/**
 * 달력 기반 복수 날짜 선택 컴포넌트
 *
 * Props:
 *   selectedDates  string[]               선택된 날짜 배열 (YYYY-MM-DD, 정렬됨)
 *   onChange       (dates: string[]) => void  선택 변경 콜백
 *   single         boolean                단일 선택 모드(기본 false). true면 클릭한 날짜 하나로 교체하고
 *                                         Shift 범위 선택·다중 칩 목록을 쓰지 않는다. 달력 디자인은 동일.
 *   singleLabel    string                 단일 모드 하단 문구의 라벨 (기본 '선택한 날짜')
 *   singleHint     string                 단일 모드 빈 상태 안내문
 */
export default function MultiDatePicker({
  selectedDates = [], onChange,
  single = false,
  singleLabel = '선택한 날짜',
  singleHint = '날짜를 클릭해 선택하세요',
}) {
  // todayKST, nowKST: utils/date.js 사용 (new Date 직접 사용 금지)
  const todayYmd = todayKST();
  const kstNow   = nowKST();

  // 현재 보고 있는 연월 (0-indexed month)
  //   single 모드에서만: 선택된 날짜가 있으면 그 달로 열어 과거/미래 정산일이 바로 보이게 한다.
  //   (기존 다중 모드는 종전대로 오늘 달로 시작 — 무회귀)
  const initialView = (() => {
    const first = single && selectedDates.length > 0 ? String(selectedDates[0]) : null;
    if (first && /^\d{4}-\d{2}-\d{2}$/.test(first)) {
      return { y: Number(first.slice(0, 4)), m: Number(first.slice(5, 7)) - 1 };
    }
    return { y: kstNow.getUTCFullYear(), m: kstNow.getUTCMonth() };
  })();
  const [viewYear, setViewYear] = useState(initialView.y);
  const [viewMonth, setViewMonth] = useState(initialView.m);

  // Shift+클릭 범위 선택의 기준점 날짜
  const [lastClicked, setLastClicked] = useState(null);

  const selectedSet = new Set(selectedDates);
  const { firstDay, daysInMonth } = buildCalendarMeta(viewYear, viewMonth);

  // ── 월 이동 ──────────────────────────────────────────────────────────────
  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  // ── 날짜 클릭 ────────────────────────────────────────────────────────────
  function handleDayClick(ymd, isShift) {
    // 단일 선택 모드: 클릭한 날짜 하나로 교체 (토글·범위 없음)
    if (single) {
      onChange([ymd]);
      setLastClicked(ymd);
      return;
    }
    if (isShift && lastClicked) {
      // Shift+클릭: lastClicked ~ ymd 사이 범위 일괄 토글
      const a = parseYmd(lastClicked);
      const b = parseYmd(ymd);
      const [start, end] = a <= b ? [a, b] : [b, a];

      // toDateOnlyUtc: utils/date.js 사용 (new Date 직접 사용 금지)
      const rangeDates = [];
      const cursor = toDateOnlyUtc(start);
      while (cursor <= end) {
        rangeDates.push(toYmd(cursor.getUTCFullYear(), cursor.getUTCMonth(), cursor.getUTCDate()));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }

      // 범위 내 날짜가 모두 선택된 상태면 해제, 아니면 모두 추가
      const allSelected = rangeDates.every(d => selectedSet.has(d));
      let next;
      if (allSelected) {
        const removeSet = new Set(rangeDates);
        next = selectedDates.filter(d => !removeSet.has(d));
      } else {
        next = Array.from(new Set([...selectedDates, ...rangeDates])).sort();
      }
      onChange(next);
    } else {
      // 단일 클릭: 토글
      const next = selectedSet.has(ymd)
        ? selectedDates.filter(d => d !== ymd)
        : [...selectedDates, ymd].sort();
      onChange(next);
    }
    setLastClicked(ymd);
  }

  function removeDate(ymd) { onChange(selectedDates.filter(d => d !== ymd)); }
  function clearAll() { onChange([]); setLastClicked(null); }

  // 달력 셀 배열: 앞쪽 빈 칸(null) + 날짜 숫자
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={pickerWrapStyle}>
      {/* 월 네비게이션 헤더 */}
      <div style={navRowStyle}>
        <button type="button" style={navBtnStyle} onClick={prevMonth}>‹</button>
        <span style={monthLabelStyle}>{viewYear}년 {viewMonth + 1}월</span>
        <button type="button" style={navBtnStyle} onClick={nextMonth}>›</button>
      </div>

      {/* 요일 헤더 + 날짜 그리드 */}
      <div style={gridStyle}>
        {/* 요일 이름 (일~토) */}
        {WEEKDAY_LABELS.map((label, i) => (
          <div key={i} style={weekdayHeaderStyle(i)}>{label}</div>
        ))}

        {/* 날짜 셀 */}
        {cells.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;
          const ymd = toYmd(viewYear, viewMonth, day);
          const isSelected = selectedSet.has(ymd);
          const isToday = ymd === todayYmd;
          const dow = (firstDay + day - 1) % 7; // 0=일, 6=토
          return (
            <button
              key={ymd}
              type="button"
              style={dayCellStyle(isSelected, isToday, dow)}
              onClick={(e) => handleDayClick(ymd, e.shiftKey)}
              title={`${ymd}${isSelected ? ' (선택됨)' : ''}`}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* 단일 선택 모드: 선택 날짜 한 줄 (칩 목록·모두 지우기 없음) */}
      {single ? (
        <div style={emptyHintStyle}>
          {selectedDates.length > 0
            ? <span style={{ fontWeight: 700, color: '#6366f1' }}>{singleLabel} {selectedDates[0]}</span>
            : singleHint}
        </div>
      ) : selectedDates.length > 0 ? (
        <div style={selectedSectionStyle}>
          <div style={selectedHeaderStyle}>
            <span style={{ fontWeight: 700, fontSize: 12, color: '#6366f1' }}>
              선택된 날짜 {selectedDates.length}개
            </span>
            <button type="button" style={clearBtnStyle} onClick={clearAll}>모두 지우기</button>
          </div>
          <div style={chipsWrapStyle}>
            {selectedDates.map(ymd => (
              <span key={ymd} style={dateChipStyle}>
                {ymd}
                <button type="button" style={chipXBtnStyle} onClick={() => removeDate(ymd)}>×</button>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div style={emptyHintStyle}>날짜를 클릭하여 선택 · Shift+클릭으로 범위 선택</div>
      )}
    </div>
  );
}

// ── 스타일 ────────────────────────────────────────────────────────────────────
const pickerWrapStyle = {
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '12px 14px',
  background: 'var(--bg-subtle)',
  userSelect: 'none',
};
const navRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 10,
};
const navBtnStyle = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '4px 12px',
  background: 'var(--bg-card)',
  cursor: 'pointer',
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
  color: 'var(--text-sub)',
};
const monthLabelStyle = { fontWeight: 800, fontSize: 15, color: 'var(--text-sub)' };
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 3,
};
// 요일 헤더: 일요일 빨강, 토요일 파랑
const weekdayHeaderStyle = (i) => ({
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 700,
  padding: '3px 0 5px',
  color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : 'var(--text-muted)',
});
// 날짜 셀: 선택 여부/오늘/요일에 따라 스타일 분기
const dayCellStyle = (isSelected, isToday, dow) => ({
  border: isToday && !isSelected ? '2px solid #6366f1' : '1px solid transparent',
  borderRadius: 7,
  padding: '6px 0',
  cursor: 'pointer',
  textAlign: 'center',
  fontSize: 13,
  background: isSelected ? '#6366f1' : '#fff',
  color: isSelected ? '#fff' : dow === 0 ? '#ef4444' : dow === 6 ? '#3b82f6' : 'var(--text-sub)',
  fontWeight: isToday ? 800 : 400,
  transition: 'background 0.1s, transform 0.05s',
});
const selectedSectionStyle = {
  marginTop: 10,
  borderTop: '1px solid var(--border)',
  paddingTop: 8,
};
const selectedHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 6,
};
const clearBtnStyle = {
  border: 'none',
  background: 'none',
  color: '#dc2626',
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 700,
  padding: '2px 4px',
};
const chipsWrapStyle = { display: 'flex', flexWrap: 'wrap', gap: 4 };
const dateChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: '#e0e7ff',
  color: '#3730a3',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 12,
  fontWeight: 600,
};
const chipXBtnStyle = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#6366f1',
  fontWeight: 700,
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
};
const emptyHintStyle = {
  marginTop: 8,
  fontSize: 12,
  color: 'var(--text-muted)',
  textAlign: 'center',
  padding: '4px 0 2px',
};
